import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Rocket, LineChart, Code2, Play, Check, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, Shield, Activity
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const TechStartup: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Rocket;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Rocket;
  };

  const schoolName = previewData?.name || "DevHigh";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2070&auto=format&fit=crop"],
      tagline: "Building the Modern Maker",
      titlePrefix: "Ship. Build.",
      subtitle: "The ultimate incubator for future founders. We combine Ivy League standards with Silicon Valley hustle to prepare you for the real world."
    },
    features: [
      { icon: "Code2", title: "Project-First", desc: "No traditional exams. You graduate with a portfolio of 10+ live products." },
      { icon: "Zap", title: "Industry Sync", desc: "Direct weekly mentorship from engineers at Google, Meta, and Stripe." },
      { icon: "Globe", title: "Venture Ready", desc: "Access to early-stage capital and a global network of angel investors." }
    ],
    programs: [
      { title: "Full-Stack Ops", age: "Years 9-11", desc: "Mastering the fundamentals of architecture and decentralization.", img: "https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=2070&auto=format&fit=crop" },
      { title: "Founder Track", age: "Years 12-13", desc: "Advanced business logic, fundraising, and product-market fit.", img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop" }
    ],
    stats: [
        { value: "450k", label: "AVG. SALARY" },
        { value: "100+", label: "STARTUPS BORN" },
        { value: "15", label: "COUNTRIES ACTIVE" }
    ],
    news: [
        { title: "Series A Success", date: "MAR 10", desc: "Our alumni's fintech startup raises $12M from major VCs.", img: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'STACK', href: '#features' },
        { label: 'TRACKS', href: '#programs' },
        { label: 'STARTUPS', href: '#gallery' },
        { label: 'OPS', href: '#contact' }
    ],
    team: [
        { name: "Sarah Chen", role: "CEO / Head of School", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop" },
        { name: "Marcus Vane", role: "CTO / Academic Lead", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Labs", img: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop", title: "Open Ops Center" },
        { category: "Events", img: "https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?q=80&w=2070&auto=format&fit=crop", title: "Demo Day 26" }
    ],
    facilities: [
        { title: "Hustle Hub", desc: "24/7 collaborative workspace with gigabit fiber and private pods.", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2070&auto=format&fit=crop", icon: "Layout" },
        { title: "The Studio", desc: "Professional media lab for podcasting and video production.", img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=2070&auto=format&fit=crop", icon: "Camera" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "FOR THE NEXT 1%.",
          description: previewData?.about?.description || schoolName + " is a high-performance training ground for the next generation of builders. We believe that the biggest risk is not taking one.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1542744094-24638eff58bb?q=80&w=2071&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "DevHigh_Whitepaper.pdf", size: "1.2 MB", type: "PDF" },
          { title: "Founder_Manual.v1.pdf", size: "4.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "BUILDER BOULEVARD, TECH CITY, NAIROBI",
        phone: previewData?.contact?.phone || "+254 700 800 900",
        email: previewData?.contact?.email || "hello@devhigh.so",
        officeHours: previewData?.contact?.officeHours || "24/7 ASYNC SUPPORT",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Build Logs & Docs",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Digital Assets Repos",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#4f46e5');
        root.style.setProperty('--color-secondary', colors.secondary || '#7c3aed');
        root.style.setProperty('--color-accent', colors.accent || '#818cf8');
        root.style.setProperty('--color-background', colors.background || '#ffffff');
        root.style.setProperty('--color-text', colors.text || '#0f172a');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Plus Jakarta Sans'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=Inter:wght@400;500;600;700&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Decommission module?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="tech-startup bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Modern SaaS Nav */}
      <header className="fixed w-full z-[100] bg-white/70 backdrop-blur-xl border-b border-slate-100">
          <div className="container mx-auto px-6 h-20 flex justify-between items-center">
              <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-xl flex items-center justify-center text-white shadow-lg">
                      <Code2 size={24} />
                  </div>
                  <span className="text-xl font-extrabold tracking-tight text-slate-900 uppercase">
                      <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                  </span>
              </div>
              
              <div className="hidden lg:flex items-center gap-10">
                  <nav className="flex gap-8 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                      {mockData.nav.filter(n => n.visible).map((n, i) => (
                          <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-2 group">
                             <div className="w-1 h-1 bg-[var(--color-primary)] rounded-full opacity-0 group-hover:opacity-100" />
                             {n.label}
                          </a>
                      ))}
                  </nav>
                  <Button className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white font-bold rounded-full px-8 h-12 text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100">
                      Sign In
                  </Button>
              </div>

              <button className="lg:hidden text-slate-900" onClick={() => setMenuOpen(!menuOpen)}>
                  {menuOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
          </div>
      </header>

      {/* Hero Section - The Terminal */}
      {mockData.sectionVisibility.hero && (
      <section className="relative pt-48 pb-32 overflow-hidden">
          {/* Subtle Ambient Glows */}
          <div className="absolute top-[-100px] left-[50%] -translate-x-1/2 w-[800px] h-[400px] bg-[var(--color-primary)]/10 blur-[120px] rounded-full pointer-events-none" />
          
          <div className="container mx-auto px-6 relative z-10 text-center">
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-slate-50 border border-slate-100 text-[10px] font-bold uppercase tracking-[0.2em] mb-12 text-slate-600">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
              </div>
              <h1 className="text-6xl md:text-9xl font-extrabold tracking-[-0.04em] leading-[0.9] mb-12 text-slate-900">
                  <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)]">Modern Maker.</span>
              </h1>
              <p className="text-xl md:text-2xl font-medium text-slate-400 max-w-2xl mx-auto mb-16 leading-relaxed">
                  <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-6 mb-32">
                  <Button className="h-16 px-12 bg-[var(--color-primary)] text-white rounded-2xl font-bold uppercase tracking-widest text-xs shadow-2xl shadow-indigo-200 hover:scale-105 transition-all">
                      Deploy Your Future
                  </Button>
                  <Button variant="outline" className="h-16 px-12 border-slate-200 text-slate-700 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-slate-50 transition-all">
                      Watch Demo <Play className="ml-2 w-4 h-4 fill-slate-700" />
                  </Button>
              </div>

              {/* Product Screenshot Mockup */}
              <div className="max-w-6xl mx-auto relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[var(--color-primary)] to-purple-400 rounded-[32px] blur opacity-10 group-hover:opacity-20 transition-all duration-1000" />
                  <div className="relative bg-white border border-slate-200 rounded-[28px] overflow-hidden shadow-2xl">
                        <div className="h-12 bg-slate-50 border-b border-slate-200 flex items-center px-6 gap-2">
                             {[1,2,3].map(i => <div key={i} className="w-2.5 h-2.5 rounded-full bg-slate-200" />)}
                             <div className="mx-auto w-1/3 h-5 bg-white rounded-md border border-slate-200" />
                        </div>
                       <VisualImageEditable 
                          src={mockData.hero.backgroundImage} 
                          className="w-full aspect-video object-cover" 
                          alt="Main Visual"
                          onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                          isEditMode={!!isEditMode}
                      />
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Feature Grid - The Stack */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-40 border-y border-slate-100 bg-slate-50/50">
          <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="bg-white p-12 rounded-[32px] border border-slate-100 group hover:shadow-2xl hover:shadow-indigo-100 transition-all duration-500 relative">
                          <div className="w-16 h-16 bg-[var(--color-primary)]/5 rounded-2xl flex items-center justify-center mb-10 text-[var(--color-primary)] group-hover:scale-110 transition-transform">
                              {React.createElement(item.icon, { size: 32 })}
                          </div>
                          <h3 className="text-2xl font-extrabold text-slate-900 mb-6 tracking-tight">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-slate-400 leading-relaxed font-medium mb-10">
                            <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <div className="w-12 h-1 bg-slate-100 group-hover:bg-[var(--color-primary)] group-hover:w-full transition-all" />
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute bottom-4 right-4 text-[10px] font-bold text-slate-200 hover:text-red-500">De-stack</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "NEW FEATURE", desc: "SaaS module description.", icon: "Check" })} className="border-4 border-dashed border-slate-200 rounded-[32px] flex flex-col items-center justify-center p-12 cursor-pointer hover:bg-slate-50 transition-all text-slate-300">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Program Modules - The Roadmap */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40">
          <div className="container mx-auto px-6">
              <div className="text-center mb-32 max-w-2xl mx-auto">
                  <span className="text-[var(--color-primary)] font-extrabold tracking-[0.3em] uppercase text-xs mb-6 block underline decoration-indigo-200 underline-offset-8">THE CURRICULUM ROADMAP</span>
                  <h2 className="text-5xl md:text-7xl font-extrabold text-slate-900 leading-none tracking-tight">Level Up Your <br/> <span className="text-slate-300">Technical Ops.</span></h2>
              </div>

              <div className="space-y-12">
                  {mockData.programs.map((p, i) => (
                      <div key={i} className={`flex flex-col lg:flex-row gap-20 items-center p-12 rounded-[40px] border border-slate-100 hover:bg-slate-50 transition-all duration-700 ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                          <div className="w-full lg:w-1/2 aspect-video rounded-3xl overflow-hidden shadow-2xl relative group">
                               <VisualImageEditable 
                                  src={p.img} alt={p.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s]"
                                  onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-6 left-6 px-4 py-2 bg-white/90 backdrop-blur-md rounded-full text-[10px] font-black uppercase text-[var(--color-primary)] border border-slate-100 shadow-sm">{p.age}</div>
                          </div>
                          <div className="w-full lg:w-1/2">
                               <div className="w-16 h-1 w-20 bg-[var(--color-primary)] mb-10" />
                               <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-8 tracking-tight">
                                 <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                               </h3>
                               <p className="text-xl text-slate-400 font-medium leading-relaxed mb-12 italic">
                                 <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                               </p>
                               <div className="flex gap-4">
                                   <Button className="bg-slate-900 text-white rounded-xl h-14 px-10 font-bold uppercase tracking-widest text-[9px] hover:bg-slate-800">Syllabus.json</Button>
                                   <Button variant="ghost" className="text-slate-400 font-bold uppercase tracking-widest text-[9px] hover:text-[var(--color-primary)] group">View Projects <ArrowRight size={16} className="ml-4 group-hover:translate-x-2 transition-transform"/></Button>
                               </div>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="text-red-500 font-extrabold text-[10px] uppercase">Archive Project</button>}
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Stats - Traction */}
      {mockData.sectionVisibility.stats && (
      <section className="py-32 container mx-auto px-6 border-b border-slate-100">
          <div className="flex flex-wrap justify-between items-center gap-20">
              {mockData.stats.map((s, i) => (
                  <div key={i} className="flex-1 min-w-[200px] text-center lg:text-left">
                      <div className="text-5xl md:text-7xl font-extrabold text-[var(--color-primary)] mb-4 tracking-[-0.04em]">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.6em] text-slate-300">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Gallery - The Portfolio */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40 px-6">
           <div className="container mx-auto">
                <div className="flex justify-between items-end mb-24 gap-8">
                     <div>
                          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">Launched on DevHigh.</h2>
                          <p className="text-slate-400 font-medium italic">Our students don't just study code, they launch ecosystems.</p>
                     </div>
                     <Button variant="outline" className="border-slate-200 text-slate-600 rounded-full px-8 h-12 text-[10px] font-black uppercase tracking-widest">Full Portfolio</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                     {mockData.gallery.map((item, i) => (
                         <div key={i} className="group relative rounded-[40px] overflow-hidden border border-slate-100 shadow-xl bg-white transition-all hover:scale-[1.02]">
                              <div className="aspect-video overflow-hidden">
                                   <VisualImageEditable 
                                      src={item.img} alt={item.title}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000"
                                      onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                                      isEditMode={!!isEditMode}
                                  />
                              </div>
                              <div className="p-12 flex justify-between items-center bg-white">
                                  <div>
                                       <h4 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none mb-2">{item.title}</h4>
                                       <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{item.category}</p>
                                  </div>
                                  <div className="w-14 h-14 border border-slate-100 rounded-full flex items-center justify-center text-slate-300 group-hover:bg-[var(--color-primary)] group-hover:text-white transition-all shadow-sm">
                                      <ArrowUpRight size={28} />
                                  </div>
                              </div>
                         </div>
                     ))}
                </div>
           </div>
      </section>
      )}

      {/* News - The Sprint Log */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 bg-slate-50 px-6">
          <div className="container mx-auto">
              <div className="flex justify-between items-baseline mb-24 border-b-2 border-slate-100 pb-12">
                   <h2 className="text-6xl md:text-9xl font-extrabold -tracking-[0.05em] text-slate-900 opacity-10">SPRINT_LOG</h2>
                   <div className="flex items-center gap-4 text-[10px] font-bold uppercase text-slate-400 tracking-widest">VER 2.6.0.44 <div className="h-4 w-px bg-slate-200" /> STABLE</div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group bg-white p-12 rounded-[40px] border border-slate-200 shadow-sm hover:shadow-2xl transition-all duration-500">
                          <div className="text-[10px] font-black text-[var(--color-primary)] uppercase tracking-widest mb-10 italic">DATA_PACKET // {item.date}</div>
                          <h3 className="text-3xl font-extrabold text-slate-900 mb-10 leading-[1.1] tracking-tight group-hover:text-[var(--color-primary)] transition-colors">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="aspect-[4/3] rounded-3xl overflow-hidden mb-12">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <p className="text-slate-400 font-medium leading-relaxed mb-12 line-clamp-3 italic">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <Send className="text-slate-200 group-hover:text-[var(--color-primary)] group-hover:translate-x-4 transition-all" size={32} />
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads - The Asset Vault */}
      {/* Downloads / Repository */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-40 bg-white">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-24 gap-8">
                  <div className="max-w-xl">
                      <span className="text-[var(--color-primary)] font-extrabold tracking-[0.3em] uppercase text-xs mb-6 block">
                          <VisualTextEditable 
                              value={mockData.labels.downloadsSection} 
                              onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                              isEditMode={!!isEditMode} 
                              as="span" 
                          />
                      </span>
                      <h2 className="text-5xl md:text-7xl font-extrabold text-slate-900 leading-none tracking-tight">
                        <VisualTextEditable 
                            value={mockData.labels.downloadsTitle} 
                            onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                            isEditMode={!!isEditMode} 
                            as="span" 
                        />
                      </h2>
                  </div>
                  {isEditMode && (
                      <Button onClick={() => addArrayItem('downloads', { title: "New_System_Sheet.pdf", size: "1.0 MB", type: "PDF" })} className="bg-slate-900 text-white rounded-2xl h-16 px-10 font-bold uppercase tracking-widest text-[10px] hover:bg-slate-800 shadow-2xl transition-all">
                          PUSH_ASSET +
                      </Button>
                  )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group bg-slate-50 border border-slate-100 p-8 rounded-[32px] hover:bg-white hover:shadow-2xl hover:shadow-indigo-100 transition-all duration-500 relative">
                          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 text-[var(--color-primary)] shadow-sm group-hover:scale-110 group-hover:bg-[var(--color-primary)] group-hover:text-white transition-all">
                              <Download size={28} />
                          </div>
                          <h4 className="text-xl font-extrabold text-slate-800 mb-4 tracking-tight leading-tight">
                              <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h4>
                          <div className="flex items-center gap-3 mb-10">
                              <span className="px-3 py-1 bg-white border border-slate-100 rounded-full text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                  <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} />
                              </span>
                              <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                                  <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} />
                              </span>
                          </div>
                          <button className="w-full py-4 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-[var(--color-primary)] transition-all flex items-center justify-center gap-4">
                              FETCH_ASSET <ArrowRight size={16} />
                          </button>
                          {isEditMode && (
                              <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 w-10 h-10 bg-white border border-slate-100 rounded-full flex items-center justify-center text-slate-200 hover:text-red-500 transition-colors shadow-sm">
                                  <X size={18} />
                              </button>
                          )}
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Final Deployment */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-[var(--color-primary)] text-white text-center px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" />
          <div className="container mx-auto relative z-10 max-w-5xl">
              <span className="text-indigo-200 font-extrabold tracking-[0.5em] uppercase text-xs mb-10 block">System Integration Active</span>
              <h2 className="text-6xl md:text-[10rem] font-extrabold leading-none -tracking-[0.04em] mb-20 shadow-2xl">
                 Join the <br/> <span className="bg-white text-[var(--color-primary)] px-8 italic">Network.</span>
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-8">
                   <Button className="bg-white text-[var(--color-primary)] rounded-full h-24 px-16 font-extrabold uppercase tracking-widest text-xs hover:scale-110 transition-all shadow-2xl">
                       Initialize Application
                   </Button>
                   <Button variant="outline" className="border-white/20 text-white rounded-full h-24 px-16 font-extrabold uppercase tracking-widest text-xs hover:bg-white hover:text-[var(--color-primary)] transition-all">
                       Download Manifesto
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Ops Center */}
      <footer id="contact" className="bg-white text-slate-900 pt-40 pb-20 px-6 border-t border-slate-100">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-6">
                      <div className="flex items-center gap-4 mb-10">
                           <div className="w-16 h-16 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-2xl flex items-center justify-center text-white shadow-xl">
                               <Rocket size={32} />
                           </div>
                           <h2 className="text-4xl font-extrabold uppercase tracking-tighter">{schoolName}.</h2>
                      </div>
                      <p className="text-3xl font-extrabold leading-tight mb-20 -tracking-[0.02em] text-slate-200">Constructing the architects of tomorrow, today.</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all shadow-sm"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-slate-300 mb-16">SYSTEMS</h4>
                      <ul className="space-y-8 text-xs font-extrabold uppercase tracking-widest text-slate-900">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-6 group">
                                <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] opacity-0 group-hover:opacity-100" />
                                {item.label}
                                </a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-4">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-slate-300 mb-16">COORDINATES</h4>
                      <div className="space-y-16">
                          <div className="flex gap-10 group">
                              <MapPin size={32} className="text-slate-200 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-lg font-extrabold italic uppercase tracking-tight text-slate-900 leading-none">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-10 group">
                              <Phone size={32} className="text-slate-200 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-lg font-extrabold tracking-widest text-slate-900">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-bold uppercase tracking-[0.6em] text-slate-200">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // NETWORK_ID: DEV_09 // ACCELERATED_STATUS</p>
                  <div className="flex gap-12 italic underline underline-offset-8 decoration-slate-100">
                      <a href="#" className="hover:text-slate-900">Protocol.v1</a>
                      <a href="#" className="hover:text-slate-900">Usage.sys</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* High-Velocity Full UI Overlay */}
      {menuOpen && (
          <div className="fixed inset-0 z-[1000] bg-white p-12 flex flex-col animate-in fade-in zoom-in duration-500 overflow-hidden">
               <div className="container mx-auto h-full flex flex-col relative z-20">
                   <div className="flex justify-between items-center mb-24 border-b border-slate-100 pb-12">
                       <h2 className="text-4xl font-extrabold uppercase tracking-tighter leading-none text-slate-900">{schoolName}</h2>
                       <button onClick={() => setMenuOpen(false)} className="text-slate-900"><X size={64} /></button>
                   </div>
                   <nav className="flex flex-col gap-10">
                       {mockData.nav.filter(n => n.visible).map((n, i) => (
                           <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-7xl md:text-9xl font-extrabold uppercase tracking-tight text-slate-100 hover:text-[var(--color-primary)] hover:translate-x-12 transition-all">
                               {n.label}
                           </a>
                       ))}
                   </nav>
                   <div className="mt-auto flex justify-between items-end border-t border-slate-100 pt-12">
                        <p className="text-[10px] font-black uppercase tracking-[0.8em] text-slate-300">SYSTEMS_OPTIMIZED</p>
                        <div className="text-[20rem] font-black text-slate-50 leading-none">HUB</div>
                   </div>
               </div>
          </div>
      )}
    </div>
  );
};

export default TechStartup;
